import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  Button,
  Tooltip as MuiTooltip,
  Chip,
  Card,
  Skeleton,
  Alert,
  AlertTitle,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import SpeedIcon from "@mui/icons-material/Speed";
import TimelineIcon from "@mui/icons-material/Timeline";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StarIcon from "@mui/icons-material/Star";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchoolIcon from "@mui/icons-material/School";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import i18n from "../../i18";
import Cookies from "js-cookie";
import kurdish from "../Images/flag.png";
import iraq from "../Images/iraq.png";
import UK from "../Images/united-kingdom.png";

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState({
    students: 0,
    teachers: 0,
    uniforms: 0,
    books: 0,
    storage_usage: 0,
    monthly_payments: [],
    income_vs_expense: [],
    low_stock: { books: [], uniforms: [] },
    top_books: [],
    top_uniforms: [],
    teacher_salary_trends: [],
  });
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [language, setLanguage] = useState("English");
  const [anchorEl, setAnchorEl] = useState(null);
  
  const token = localStorage.getItem("token");

  // Language switching logic
  useEffect(() => {
    const savedLang = Cookies.get("language");
    if (savedLang) {
      setLanguage(savedLang);
      i18n.changeLanguage(savedLang);
    }
  }, []);

  const onChangeLang = (selectedLang) => {
    setLanguage(selectedLang);
    i18n.changeLanguage(selectedLang);
    Cookies.set("language", selectedLang);
    localStorage.removeItem("i18nextLng");
  };

  const handleLanguageMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = (language) => {
    onChangeLang(language);
    setAnchorEl(null);
  };

  // Enhanced data fetching with caching and error handling
  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await axios.get(
        "https://api.clever-school.com/api/reports",
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          }
        }
      );

      setAnalyticsData(response.data);
      setLastUpdated(new Date());
      setError("");
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        setError(t("please check your internet and try again"));
      }
      console.error("Error fetching analytics data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, t]);

  useEffect(() => {
    if (token) {
      fetchData();
    } else {
      window.location.href = "/login";
    }
  }, [token, fetchData]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  // Export functionality

  // Enhanced stats with modern design inspired by the reference
  const stats = useMemo(() => [
    {
      title: t("total_students"),
      value: analyticsData.students,
      icon: <SchoolIcon />,
      bgColor: "#ffffff",
      trend: "+12%",
      trendUp: true,
      subtitle: t("enrolled_students"),
      color: "#4CAF50",
      description: t("active_enrollment"),
      trendColor: "#4CAF50"
    },
    {
      title: t("teachers"),
      value: analyticsData.teachers,
      icon: <GroupsIcon />,
      bgColor: "#ffffff",
      trend: "+5%",
      trendUp: true,
      subtitle: t("teaching_staff"),
      color: "#2196F3",
      description: t("qualified_educators"),
      trendColor: "#4CAF50"
    },
    {
      title: t("uniforms"),
      value: analyticsData.uniforms,
      icon: <CheckroomIcon />,
      bgColor: "#ffffff",
      trend: "-3%",
      trendUp: false,
      subtitle: t("in_stock"),
      color: "#FF9800",
      description: t("available_items"),
      trendColor: "#F44336"
    },
    {
      title: t("books"),
      value: analyticsData.books,
      icon: <MenuBookIcon />,
      bgColor: "#ffffff",
      trend: "+8%",
      trendUp: true,
      subtitle: t("available_books"),
      color: "#9C27B0",
      description: t("educational_resources"),
      trendColor: "#4CAF50"
    },
  ], [analyticsData, t]);

  // Enhanced loading skeleton
  const LoadingSkeleton = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton variant="rectangular" width={100} height={40} />
          <Skeleton variant="rectangular" width={100} height={40} />
        </Box>
      </Box>
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item}>
            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  if (loading && !refreshing) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={handleRefresh}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        {/* Clean White Header */}
        <Card
          elevation={1}
          sx={{
            mb: 4,
            background: "#ffffff",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center", 
              flexWrap: "wrap",
              gap: 3
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: "#f8f9fa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#2c3e50",
                    border: "1px solid #e9ecef"
                  }}
                >
                  <DashboardIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ 
                      fontWeight: 700, 
                      color: "#2c3e50",
                      mb: 0.5,
                      fontSize: "1.75rem"
                    }}
                  >
                    {t("school_dashboard")}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: "#6c757d", 
                    fontSize: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}>
                    <VisibilityIcon sx={{ fontSize: 16, color: "#6c757d" }} />
                    {lastUpdated && `${t("last_updated")}: ${lastUpdated.toLocaleTimeString()}`}
                  </Typography>
                </Box>
              </Box>
          
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                {/* Quick Access Buttons */}
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <MuiTooltip title={t("view_students")}>
                    <Button
                      variant="outlined"
                      startIcon={<SchoolIcon />}
                      size="small"
                      onClick={() => navigate("/GetStudent")}
                      sx={{
                        borderColor: "#dee2e6",
                        color: "#2c3e50",
                        "&:hover": {
                          borderColor: "#adb5bd",
                          backgroundColor: "#f8f9fa"
                        }
                      }}
                    >
                      {t("students")}
                    </Button>
                  </MuiTooltip>

                  <MuiTooltip title={t("view_teachers")}>
                    <Button
                      variant="outlined"
                      startIcon={<GroupsIcon />}
                      size="small"
                      onClick={() => navigate("/GetTeacher")}
                      sx={{
                        borderColor: "#dee2e6",
                        color: "#2c3e50",
                        "&:hover": {
                          borderColor: "#adb5bd",
                          backgroundColor: "#f8f9fa"
                        }
                      }}
                    >
                      {t("teachers")}
                    </Button>
                  </MuiTooltip>

                  <MuiTooltip title={t("view_reports")}>
                    <Button
                      variant="outlined"
                      startIcon={<AssessmentIcon />}
                      size="small"
                      onClick={() => navigate("/Raptors")}
                      sx={{
                        borderColor: "#dee2e6",
                        color: "#2c3e50",
                        "&:hover": {
                          borderColor: "#adb5bd",
                          backgroundColor: "#f8f9fa"
                        }
                      }}
                    >
                      {t("reports")}
                    </Button>
                  </MuiTooltip>

                  <MuiTooltip title={t("view_uniforms")}>
                    <Button
                      variant="outlined"
                      startIcon={<CheckroomIcon />}
                      size="small"
                      onClick={() => navigate("/GetProducts")}
                      sx={{
                        borderColor: "#dee2e6",
                        color: "#2c3e50",
                        "&:hover": {
                          borderColor: "#adb5bd",
                          backgroundColor: "#f8f9fa"
                        }
                      }}
                    >
                      {t("uniforms")}
                    </Button>
                  </MuiTooltip>

                  <MuiTooltip title={t("view_books")}>
                    <Button
                      variant="outlined"
                      startIcon={<MenuBookIcon />}
                      size="small"
                      onClick={() => navigate("/get-books")}
                      sx={{
                        borderColor: "#dee2e6",
                        color: "#2c3e50",
                        "&:hover": {
                          borderColor: "#adb5bd",
                          backgroundColor: "#f8f9fa"
                        }
                      }}
                    >
                      {t("books")}
                    </Button>
                  </MuiTooltip>
                </Box>

                {/* Language Switch Button */}
                <MuiTooltip title={t("language")}>
                  <Button
                    variant="outlined"
                    onClick={handleLanguageMenuClick}
                    sx={{
                      borderColor: "#dee2e6",
                      color: "#2c3e50",
                      "&:hover": {
                        borderColor: "#adb5bd",
                        backgroundColor: "#f8f9fa"
                      },
                      display: "flex",
                      alignItems: "center",
                      gap: 1
                    }}
                  >
                    <img 
                      src={language === "en" ? UK : language === "ku" ? kurdish : iraq} 
                      alt={language === "en" ? "English" : language === "ku" ? "Kurdish" : "Arabic"} 
                      width={20} 
                      height={20} 
                    />
                    {language === "en" ? "English" : language === "ku" ? "کوردی" : "العربية"}
                  </Button>
                </MuiTooltip>

                {/* Language Menu */}
                <Menu
                  id="language-select-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  MenuListProps={{ sx: { padding: 0 } }}
                >
                  <MenuItem onClick={() => handleLanguageMenuClose("en")}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <img src={UK} alt="UK Flag" width={30} height={30} />
                      <span style={{ marginLeft: "10px" }}>English</span>
                    </div>
                  </MenuItem>
                  <MenuItem onClick={() => handleLanguageMenuClose("ku")}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <img src={kurdish} alt="Kurdish Flag" width={30} height={30} />
                      <span style={{ marginLeft: "10px" }}>کوردی</span>
                    </div>
                  </MenuItem>
                  <MenuItem onClick={() => handleLanguageMenuClose("ar")}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <img src={iraq} alt="Iraq Flag" width={30} height={30} />
                      <span style={{ marginLeft: "10px" }}>العربية</span>
                    </div>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Box>
        </Card>

      {/* Main Container */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexDirection: { xs: "column", md: "row" }, // Stack on mobile
          overflowX: { xs: "auto", md: "unset" }, // Enable horizontal scroll for mobile
        }}
      >
        {/* Statistics Grid */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {stats.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 1,
                  background: "#ffffff",
                  border: "1px solid #dee2e6",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }
                }}
              >
                <Box sx={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "flex-start",
                  mb: 2
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: `${item.color}12`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: item.color
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 500, 
                        color: "#6c757d",
                        fontSize: "0.875rem"
                      }}>
                        {item.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Trend Indicator */}
                  <Chip
                    icon={item.trendUp ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    label={item.trend}
                    size="small"
                    sx={{
                      bgcolor: item.trendUp ? "#d4edda" : "#f8d7da",
                      color: item.trendUp ? "#155724" : "#721c24",
                      border: `1px solid ${item.trendUp ? "#c3e6cb" : "#f5c6cb"}`,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      height: 28
                    }}
                  />
                </Box>
                
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: "#2c3e50", 
                    mb: 0.5,
                    fontSize: "1.75rem"
                  }}>
                    {item.value.toLocaleString()}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    color: "#6c757d", 
                    fontSize: "0.8rem"
                  }}>
                    {item.description}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>


      <br></br>
      <Grid container spacing={2}>
        {/* Monthly Payments Chart */}
        <Grid item xs={12} md={8}>
          <Card
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 1,
              background: "#ffffff",
              border: "1px solid #dee2e6",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: "#28a74512",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#28a745",
                  mr: 1.5
                }}
              >
                <AttachMoneyIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50", mb: 0.5, fontSize: "1rem" }}>
                  {t("monthly_payments")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6c757d", fontSize: "0.8rem" }}>
                  {t("revenue_trends_and_payment_analytics")}
                </Typography>
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analyticsData.monthly_payments}>
                <CartesianGrid strokeDasharray="5 5" stroke="#e0e0e0" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 8, fill: "#777" }}
                  axisLine={{ stroke: "#ccc" }}
                />
                <YAxis
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                  tick={{ fontSize: 8, fill: "#777" }}
                  axisLine={{ stroke: "#ccc" }}
                  style={{
                    fontSize: "8px",
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#333",
                    color: "#fff",
                    borderRadius: "5px",
                  }}
                  formatter={(value) => [
                    `$${value.toLocaleString()}`,
                    "Total Payments",
                  ]}
                />
                <Bar
                  dataKey="total"
                  fill="url(#gradient1)"
                  radius={[8, 8, 0, 0]} // Rounded top corners
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Define Gradient */}
          <svg width="0" height="0">
            <defs>
              <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop
                  offset="0%"
                  style={{ stopColor: "#27ae60", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#2ecc71", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>
          </svg>
        </Grid>

        {/* Income vs Expense Chart */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 1,
              background: "#ffffff",
              border: "1px solid #dee2e6",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: "#17a2b812",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#17a2b8",
                  mr: 1.5
                }}
              >
                <TimelineIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50", mb: 0.5, fontSize: "1rem" }}>
                  {t("income_vs_expense")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6c757d", fontSize: "0.8rem" }}>
                  {t("financial_performance_overview")}
                </Typography>
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analyticsData.income_vs_expense}>
                <CartesianGrid strokeDasharray="5 5" stroke="#e0e0e0" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 8, fill: "#777" }}
                  axisLine={{ stroke: "#ccc" }}
                />
                <YAxis
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                  tick={{ fontSize: 8, fill: "#777" }}
                  axisLine={{ stroke: "#ccc" }}
                  style={{
                    fontSize: "8px",
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#333",
                    color: "#fff",
                    borderRadius: "5px",
                  }}
                  formatter={(value) => [`$${value.toLocaleString()}`, "Total"]}
                />
                <Bar
                  dataKey="income"
                  fill="url(#incomeGradient)"
                  radius={[8, 8, 0, 0]} // Rounded top corners
                />
                <Bar
                  dataKey="expense"
                  fill="url(#expenseGradient)"
                  radius={[8, 8, 0, 0]} // Rounded top corners
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <svg width="0" height="0">
            <defs>
              <linearGradient
                id="incomeGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#2ecc71", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#27ae60", stopOpacity: 1 }}
                />
              </linearGradient>
              <linearGradient
                id="expenseGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#e74c3c", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#c0392b", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>
          </svg>
        </Grid>
      </Grid>
      <br></br>

      {/* Modern Alert Section */}
      <Box sx={{ mb: 4 }}>
        <Alert 
          severity="info" 
          sx={{ 
            borderRadius: 3,
            background: "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
            border: "1px solid rgba(102, 126, 234, 0.2)",
            backdropFilter: "blur(10px)",
          }}
          icon={<AnalyticsIcon />}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>{t("analytics_overview")}</AlertTitle>
          {t("dashboard_description")}
        </Alert>
      </Box>

      <Grid container spacing={2}>
        {/* Low Stock Books */}
        <Grid item xs={12} md={3}>
          <Card
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 1,
              background: "#ffffff",
              border: "1px solid #dee2e6",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: "#fd7e1412",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fd7e14",
                  mr: 1.5
                }}
              >
                <MenuBookIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50", mb: 0.5, fontSize: "1rem" }}>
                  {t("low_stock_books")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6c757d", fontSize: "0.8rem" }}>
                  {t("inventory_alerts_and_recommendations")}
                </Typography>
              </Box>
            </Box>
            <List sx={{ maxHeight: 200, overflowY: "auto" }}>
              {analyticsData.low_stock.books.map((item) => (
                <ListItem
                  key={item.id}
                  sx={{ 
                    "&:hover": { backgroundColor: "#f8f9fa" }, 
                    mb: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1
                  }}
                >
                  <ListItemText
                    primary={`${item.name} (Class ${item.class_id})`}
                    secondary={`Stock: ${item.stock}`}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      color: "#2c3e50",
                      fontSize: "0.875rem"
                    }}
                    secondaryTypographyProps={{ 
                      color: "#6c757d",
                      fontSize: "0.75rem"
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Card>
        </Grid>

        {/* Top Ordered Books */}
        <Grid item xs={12} md={9}>
          <Card
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 1,
              background: "#ffffff",
              border: "1px solid #dee2e6",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: "#28a74512",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#28a745",
                  mr: 1.5
                }}
              >
                <StarIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50", mb: 0.5, fontSize: "1rem" }}>
                  {t("top_ordered_books")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6c757d", fontSize: "0.8rem" }}>
                  {t("most_popular_educational_resources")}
                </Typography>
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analyticsData.top_books}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 10, fill: "#6c757d" }} 
                  axisLine={{ stroke: "#dee2e6" }}
                />
                <YAxis
                  tickFormatter={(value) => value.toLocaleString()}
                  tick={{ fontSize: 10, fill: "#6c757d" }}
                  axisLine={{ stroke: "#dee2e6" }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#2c3e50",
                    color: "#fff",
                    borderRadius: "6px",
                    border: "none",
                    fontSize: "12px"
                  }}
                />
                <Bar dataKey="total" fill="#28a745" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Low Stock Uniforms */}
        <Grid item xs={12} md={3}>
          <Card
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 1,
              background: "#ffffff",
              border: "1px solid #dee2e6",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: "#17a2b812",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#17a2b8",
                  mr: 1.5
                }}
              >
                <CheckroomIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50", mb: 0.5, fontSize: "1rem" }}>
                  {t("low_stock_uniforms")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6c757d", fontSize: "0.8rem" }}>
                  {t("inventory_management_alerts")}
                </Typography>
              </Box>
            </Box>
            <List sx={{ maxHeight: 200, overflowY: "auto" }}>
              {analyticsData.low_stock.uniforms.map((item) => (
                <ListItem
                  key={item.id}
                  sx={{ 
                    "&:hover": { backgroundColor: "#f8f9fa" }, 
                    mb: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1
                  }}
                >
                  <ListItemText
                    primary={`${item.name} (Gender ${item.gender})`}
                    secondary={`Stock: ${item.stock}`}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      color: "#2c3e50",
                      fontSize: "0.875rem"
                    }}
                    secondaryTypographyProps={{ 
                      color: "#6c757d",
                      fontSize: "0.75rem"
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Card>
        </Grid>

        {/* Top Ordered Uniforms */}
        <Grid item xs={12} md={9}>
          <Card
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 1,
              background: "#ffffff",
              border: "1px solid #dee2e6",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: "#6f42c112",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#6f42c1",
                  mr: 1.5
                }}
              >
                <SpeedIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50", mb: 0.5, fontSize: "1rem" }}>
                  {t("top_ordered_uniforms")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6c757d", fontSize: "0.8rem" }}>
                  {t("best_selling_uniform_items")}
                </Typography>
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analyticsData.top_uniforms}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 10, fill: "#6c757d" }} 
                  axisLine={{ stroke: "#dee2e6" }}
                />
                <YAxis
                  tickFormatter={(value) => value.toLocaleString()}
                  tick={{ fontSize: 10, fill: "#6c757d" }}
                  axisLine={{ stroke: "#dee2e6" }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#2c3e50",
                    color: "#fff",
                    borderRadius: "6px",
                    border: "none",
                    fontSize: "12px"
                  }}
                />
                <Bar dataKey="total" fill="#6f42c1" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>

      <br></br>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
        <Card
          elevation={0}
          sx={{ 
            p: 2.5, 
            borderRadius: 1,
            background: "#ffffff",
            border: "1px solid #dee2e6",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                background: "#e83e8c12",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#e83e8c",
                mr: 1.5
              }}
            >
              <AssessmentIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50", mb: 0.5, fontSize: "1rem" }}>
                {t("teacher_salary_trends")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6c757d", fontSize: "0.8rem" }}>
                {t("compensation_analysis_over_time")}
              </Typography>
            </Box>
          </Box>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={analyticsData.teacher_salary_trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 10, fill: "#6c757d" }} 
                axisLine={{ stroke: "#dee2e6" }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#6c757d" }}
                axisLine={{ stroke: "#dee2e6" }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#2c3e50",
                  color: "#fff",
                  borderRadius: "6px",
                  border: "none",
                  fontSize: "12px"
                }}
              />
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="#e83e8c" 
                strokeWidth={3}
                dot={{ fill: "#e83e8c", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: "#e83e8c", strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        </Grid>
      </Grid>
    </Box>
    </LocalizationProvider>
  );
};

export default Dashboard;
